import { useEffect, useRef, useState } from 'react';

const TYPE_MS = 42;
const DELETE_MS = 28;
const HOLD_MS = 2200;
const BETWEEN_MS = 400;

/**
 * Cycles placeholder phrases character-by-character when `enabled`.
 * Stops immediately when `enabled` becomes false (e.g. user started typing).
 */
export function useTypewriterPlaceholder(prompts: string[], enabled: boolean): string {
  const [display, setDisplay] = useState('');
  const promptsKey = prompts.join('\u0001');
  const stateRef = useRef({
    promptIndex: 0,
    charIndex: 0,
    deleting: false,
    holdUntil: 0,
  });

  useEffect(() => {
    if (!enabled || prompts.length === 0) {
      setDisplay('');
      stateRef.current = { promptIndex: 0, charIndex: 0, deleting: false, holdUntil: 0 };
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const schedule = (ms: number, fn: () => void) => {
      timer = setTimeout(() => {
        if (!cancelled) fn();
      }, ms);
    };

    const tick = () => {
      const s = stateRef.current;
      const phrase = prompts[s.promptIndex] ?? '';
      const now = Date.now();

      if (s.holdUntil > now) {
        schedule(s.holdUntil - now, tick);
        return;
      }

      if (!s.deleting) {
        if (s.charIndex < phrase.length) {
          s.charIndex += 1;
          setDisplay(phrase.slice(0, s.charIndex));
          schedule(TYPE_MS, tick);
          return;
        }
        s.holdUntil = now + HOLD_MS;
        s.deleting = true;
        schedule(HOLD_MS, tick);
        return;
      }

      if (s.charIndex > 0) {
        s.charIndex -= 1;
        setDisplay(phrase.slice(0, s.charIndex));
        schedule(DELETE_MS, tick);
        return;
      }

      s.deleting = false;
      s.promptIndex = (s.promptIndex + 1) % prompts.length;
      schedule(BETWEEN_MS, tick);
    };

    stateRef.current = { promptIndex: 0, charIndex: 0, deleting: false, holdUntil: 0 };
    setDisplay('');
    schedule(BETWEEN_MS, tick);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, promptsKey, prompts.length]);

  return display;
}
